import React from 'react';
import { Calendar, BookOpen, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../utils/localeFormatters';

/**
 * Today's Schedule Timeline
 * Vertical timeline showing today's lessons/meetings with time slots.
 *
 * Inherits direction from `<html dir>` — no per-element dir prop needed.
 * Uses CSS logical properties so LTR/RTL mirroring is automatic.
 */
export default function TodayTimeline({ meetings = [], lessons = [], centerType, isMobile, onEventClick }) {
  const { t, i18n } = useTranslation();

  // Combine events for today
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const events = [];

  meetings.forEach((m) => {
    const date = new Date(m.date);
    date.setHours(0, 0, 0, 0);
    if (date.getTime() !== today.getTime()) return;
    events.push({
      id: m.id,
      type: 'meeting',
      time: m.time || '00:00',
      title: m.studentName || m.clientName || t('dashboard.meeting', 'Meeting'),
      subtitle: m.type || m.meetingType || '',
      color: 'var(--color-primary-dark, #355760)',
      accent: 'rgba(53, 87, 96, 0.10)',
      icon: Video,
      data: m,
    });
  });

  if (centerType === 'learning') {
    lessons.forEach((l) => {
      const rawDate = l.scheduledDate || l.date;
      const date = rawDate && rawDate._seconds
        ? new Date(rawDate._seconds * 1000)
        : new Date(rawDate);
      if (isNaN(date)) return;
      date.setHours(0, 0, 0, 0);
      if (date.getTime() !== today.getTime()) return;
      events.push({
        id: l.id,
        type: 'lesson',
        time: l.time || l.startTime || '00:00',
        title: l.groupName || l.studentName || t('dashboard.lesson', 'Lesson'),
        subtitle: l.subject || '',
        color: 'var(--color-primary-dark, #355760)',
        accent: 'rgba(53, 87, 96, 0.10)',
        icon: BookOpen,
        data: l,
      });
    });
  }

  events.sort((a, b) => a.time.localeCompare(b.time));

  const now = new Date();
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const formatEventTime = (hhmm) => {
    if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return hhmm;
    const [h, m] = hhmm.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return formatTime(d, i18n.language);
  };

  return (
    <section
      aria-labelledby="today-timeline-title"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius-md, 12px)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <header
        style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Calendar size={18} color="var(--color-primary-dark)" aria-hidden="true" />
          <h3
            id="today-timeline-title"
            style={{
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('learningDashboard.timeline.title', "Today's Schedule")}
          </h3>
        </div>
        <span
          style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            backgroundColor: 'var(--color-surface-alt)',
            padding: '0.25rem 0.75rem',
            borderRadius: '999px',
            fontWeight: 500,
          }}
        >
          {events.length} {events.length === 1 ? t('dashboard.event', 'event') : t('dashboard.events', 'events')}
        </span>
      </header>

      <div
        style={{
          padding: '1rem 1.25rem',
          maxHeight: isMobile ? '320px' : '420px',
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {events.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem 1rem',
              color: 'var(--color-text-secondary)',
            }}
          >
            <Calendar size={36} style={{ opacity: 0.3, marginBottom: '0.75rem' }} aria-hidden="true" />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              {t('learningDashboard.timeline.empty', 'No lessons scheduled today')}
            </p>
          </div>
        ) : (
          <ol role="list" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {events.map((event, idx) => {
              const isPast = event.time < currentTime;
              const isCurrent = !isPast && (idx === 0 || events[idx - 1]?.time < currentTime);
              const localTime = formatEventTime(event.time);
              const interactive = typeof onEventClick === 'function';

              const Content = interactive ? 'button' : 'div';

              return (
                <li key={event.id || idx} aria-current={isCurrent ? 'time' : undefined}>
                  <Content
                    type={interactive ? 'button' : undefined}
                    onClick={interactive ? () => onEventClick(event) : undefined}
                    aria-label={`${localTime} · ${event.title}`}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: '0.75rem',
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: interactive ? 'pointer' : 'default',
                      opacity: isPast ? 0.55 : 1,
                      textAlign: 'start',
                      transition: 'opacity 150ms ease',
                    }}
                  >
                    <span
                      style={{
                        minWidth: '56px',
                        flexShrink: 0,
                        paddingBlockStart: '0.5rem',
                        fontSize: '0.8125rem',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isCurrent ? 700 : 500,
                        color: isCurrent ? 'var(--color-primary-dark)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {localTime}
                    </span>

                    <span
                      aria-hidden="true"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '16px',
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          width: isCurrent ? 12 : 8,
                          height: isCurrent ? 12 : 8,
                          borderRadius: '50%',
                          backgroundColor: isPast ? 'var(--color-border)' : event.color,
                          marginBlockStart: '0.75rem',
                          boxShadow: isCurrent ? `0 0 0 4px ${event.accent}` : 'none',
                        }}
                      />
                      {idx < events.length - 1 && (
                        <span
                          style={{
                            width: 2,
                            flex: 1,
                            backgroundColor: 'var(--color-border)',
                            marginBlockStart: '0.25rem',
                          }}
                        />
                      )}
                    </span>

                    <span
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--color-surface-alt)',
                        borderRadius: 8,
                        padding: '0.75rem 1rem',
                        borderInlineStart: `3px solid ${isCurrent ? 'var(--color-primary-dark)' : 'transparent'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        minHeight: 52,
                      }}
                    >
                      <event.icon size={18} color="var(--color-primary-dark)" style={{ flexShrink: 0 }} aria-hidden="true" />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {event.title}
                        </span>
                        {event.subtitle && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: '0.75rem',
                              color: 'var(--color-text-secondary)',
                              marginBlockStart: 2,
                            }}
                          >
                            {event.subtitle}
                          </span>
                        )}
                      </span>
                      {isCurrent && (
                        <span
                          style={{
                            fontSize: '0.625rem',
                            fontWeight: 700,
                            color: 'var(--color-on-primary, #fff)',
                            backgroundColor: 'var(--color-primary-dark)',
                            padding: '0.125rem 0.5rem',
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            flexShrink: 0,
                          }}
                        >
                          {t('learningDashboard.timeline.now', 'NOW')}
                        </span>
                      )}
                    </span>
                  </Content>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
